import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchSessions } from '../api';

const SessionsContext = createContext();

// Cache configuration
const CACHE_TTL = 30000; // 30 seconds
const POLL_INTERVAL = 60000; // 60 seconds
const STORAGE_KEY = 'myt_sessions_cache';
const STORAGE_MAX_AGE = 300000; // 5 minutes - max age for localStorage cache

/**
 * Inject preconnect hint for API domain to speed up first request
 * Done dynamically to support different URLs per environment
 */
const injectPreconnect = () => {
  const apiUrl = process.env.REACT_APP_BOOKING_API_URL;
  if (!apiUrl) return;

  try {
    const url = new URL(apiUrl);
    const origin = url.origin;

    // Check if preconnect already exists
    if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;

    // Add preconnect
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = origin;
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);

    // Add dns-prefetch as fallback
    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = origin;
    document.head.appendChild(dnsPrefetch);
  } catch (e) {
    // Ignore URL parsing errors
  }
};

// Inject preconnect immediately on module load
injectPreconnect();

/**
 * Load cached data from localStorage for instant display
 * Returns { sessions: [], customFields: {} } if no valid cache exists
 */
const loadCachedData = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const { sessions, customFields, timestamp } = JSON.parse(cached);
      // Return cached data if less than 5 minutes old
      if (Date.now() - timestamp < STORAGE_MAX_AGE && Array.isArray(sessions)) {
        return { sessions, customFields: customFields || {} };
      }
    }
  } catch (e) {
    // Ignore localStorage errors (private browsing, etc.)
  }
  return { sessions: [], customFields: {} };
};

/**
 * Save sessions and customFields to localStorage for instant load on return visits
 */
const saveCachedData = (sessions, customFields) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      sessions,
      customFields,
      timestamp: Date.now()
    }));
  } catch (e) {
    // Ignore localStorage errors (quota exceeded, private browsing, etc.)
  }
};

/**
 * SessionsProvider - Centralized sessions data management
 *
 * Features:
 * - Single source of truth for session data
 * - 30-second cache TTL to prevent redundant API calls
 * - 60-second polling for fresh data (only when tab is visible)
 * - Visibility-aware: pauses polling when tab is hidden, refreshes on focus
 */
export function SessionsProvider({ children }) {
  // Initialize from localStorage for instant display (stale-while-revalidate)
  const [sessions, setSessions] = useState(() => loadCachedData().sessions);
  const [customFields, setCustomFields] = useState(() => loadCachedData().customFields);
  // Only show loading spinner if no cached data exists
  const [loading, setLoading] = useState(() => loadCachedData().sessions.length === 0);
  const [error, setError] = useState(null);
  const lastFetchRef = useRef(0);
  const pollIntervalRef = useRef(null);
  const isVisibleRef = useRef(true);

  /**
   * Fetch sessions from API with cache check
   * @param {boolean} force - Force refresh even if cache is valid
   */
  const fetchData = useCallback(async (force = false) => {
    const now = Date.now();

    // Check cache validity (skip if forced or cache expired)
    if (!force && sessions.length > 0 && (now - lastFetchRef.current < CACHE_TTL)) {
      return;
    }

    try {
      setLoading(sessions.length === 0); // Only show loading if no data yet
      const result = await fetchSessions();

      if (result.success) {
        setSessions(result.sessions);
        setCustomFields(result.customFields);
        saveCachedData(result.sessions, result.customFields); // Persist to localStorage
        setError(null);
        lastFetchRef.current = Date.now();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  }, [sessions.length]);

  /**
   * Get a session by ID from cached data
   * @param {string} id - Session UUID
   * @returns {Object|null} Session object or null if not found
   */
  const getSessionById = useCallback((id) => {
    return sessions.find(s => s.id === id) || null;
  }, [sessions]);

  /**
   * Force refresh sessions data
   */
  const refresh = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  // Initial fetch on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Visibility-aware polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === 'visible';

      if (isVisibleRef.current) {
        // Tab became visible - refresh data
        fetchData(true);
      }
    };

    const startPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      pollIntervalRef.current = setInterval(() => {
        // Only poll if tab is visible
        if (isVisibleRef.current) {
          fetchData(true);
        }
      }, POLL_INTERVAL);
    };

    // Add visibility listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start polling
    startPolling();

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchData]);

  const value = {
    sessions,
    customFields,
    loading,
    error,
    getSessionById,
    refresh,
  };

  return (
    <SessionsContext.Provider value={value}>
      {children}
    </SessionsContext.Provider>
  );
}

/**
 * Hook to access sessions context
 * @returns {{ sessions: Array, customFields: Object, loading: boolean, error: string|null, getSessionById: Function, refresh: Function }}
 */
export function useSessions() {
  const context = useContext(SessionsContext);

  if (!context) {
    throw new Error('useSessions must be used within a SessionsProvider');
  }

  return context;
}

export default SessionsContext;
